import React, { useState, useEffect } from "react";
import { useManufacturer } from "../../contexts/WithManufacturer";
import { Product as LegacyProduct } from "../../stores/product/Product";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  useToast,
} from "randmarcomps";
import { Product as ApiProduct, usePostV4ManufacturerByRouteManufacturerIdProductAndProductNumberFieldNameMutation } from "randmar-api-client";

interface IProps {
  product: LegacyProduct | ApiProduct;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const isLegacyProduct = (p: LegacyProduct | ApiProduct): p is LegacyProduct => 'randmarSKU' in p;

const getProductSKU = (p: LegacyProduct | ApiProduct) => isLegacyProduct(p) ? p.randmarSKU : p.RandmarSKU;
const getProductMPN = (p: LegacyProduct | ApiProduct) => isLegacyProduct(p) ? p.mpn : p.MPN;

const EditPartNumber: React.FC<IProps> = ({ product, open, onOpenChange }) => {
  const { applicationId } = useManufacturer();
  const [mpn, setMpn] = useState(getProductMPN(product) || "");
  const { toast } = useToast();

  const [updateProduct, { isLoading }] = usePostV4ManufacturerByRouteManufacturerIdProductAndProductNumberFieldNameMutation();

  useEffect(() => {
    if (open) {
      setMpn(getProductMPN(product) || "");
    }
  }, [open, product]);

  const handleConfirmRequest = async () => {
    const sku = getProductSKU(product);
    if (!sku) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Product SKU is missing.",
      });
      return;
    }

    try {
      await updateProduct({
        routeManufacturerId: applicationId,
        productNumber: sku,
        fieldName: "MPN",
        newValue: mpn?.toString(),
      }).unwrap();

      toast({
        variant: "success",
        title: "Success",
        description: "Part number updated successfully.",
      });
      
      onOpenChange(false);
    } catch {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update part number.",
      });
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (isLoading) return;

    onOpenChange(isOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle>Edit Part Number</DialogTitle>
          <DialogDescription>
            Update the Manufacturer Part Number (MPN) for this product.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Skeleton className="h-4 w-[80px] justify-self-end" />
                <Skeleton className="h-10 col-span-3" />
              </div>
            </div>
            <DialogFooter>
              <Skeleton className="h-10 w-[70px]" />
              <Skeleton className="h-10 w-[85px]" />
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="product-mpn" className="text-right">
                  Part Number
                </Label>
                <Input
                  id="product-mpn"
                  value={mpn}
                  onChange={e => setMpn(e.target.value)}
                  className="col-span-3"
                  autoComplete="off"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmRequest}
                disabled={mpn === getProductMPN(product) || isLoading || mpn === ""}
              >
                Confirm
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditPartNumber;
